import React, { useState, cloneElement, isValidElement, useRef, useEffect } from 'react';
import MuiMenu from '@mui/material/Menu';
import Box from '@mui/material/Box';

export interface DropdownProps {
  overlay: React.ReactNode | (() => React.ReactNode);
  children?: React.ReactNode;
  trigger?: ('click' | 'hover' | 'contextMenu')[];
  visible?: boolean;
  open?: boolean;
  onVisibleChange?: (visible: boolean) => void;
  placement?: string;
  destroyPopupOnHide?: boolean;
  disabled?: boolean;
  overlayClassName?: string;
  overlayStyle?: React.CSSProperties;
  [key: string]: any;
}

export const Dropdown: React.FC<DropdownProps> = ({
  overlay,
  children,
  trigger = ['hover'],
  visible,
  open,
  onVisibleChange,
  disabled,
  overlayStyle,
}) => {
  const controlled = visible !== undefined || open !== undefined;
  const [innerOpen, setInnerOpen] = useState(false);
  const isOpen = controlled ? (open ?? visible ?? false) : innerOpen;

  const setOpen = (v: boolean) => {
    if (!controlled) setInnerOpen(v);
    onVisibleChange?.(v);
  };

  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const anchorRef = useRef<HTMLElement | null>(null);

  const child = isValidElement(children) ? children : <span>{children}</span>;

  const clickTrigger = trigger.includes('click');
  const hoverTrigger = trigger.includes('hover');

  useEffect(() => {
    if (isOpen && anchorRef.current) setAnchor(anchorRef.current);
  }, [isOpen]);

  const triggerEl = cloneElement(child as React.ReactElement, {
    ref: (node: HTMLElement | null) => {
      anchorRef.current = node;
      const { ref } = child as any;
      if (typeof ref === 'function') ref(node);
      else if (ref) ref.current = node;
    },
    onClick: (e: React.MouseEvent<HTMLElement>) => {
      child.props.onClick?.(e);
      if (clickTrigger && !disabled) {
        setAnchor(e.currentTarget);
        setOpen(true);
      }
    },
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      child.props.onMouseEnter?.(e);
      if (hoverTrigger && !disabled) {
        setAnchor(e.currentTarget);
        setOpen(true);
      }
    },
    onMouseLeave: (e: React.MouseEvent<HTMLElement>) => {
      child.props.onMouseLeave?.(e);
      if (hoverTrigger && !disabled) setOpen(false);
    },
    style: { cursor: disabled ? 'not-allowed' : 'pointer', ...child.props.style },
  });

  const overlayNode = typeof overlay === 'function' ? overlay() : overlay;

  return (
    <>
      {triggerEl}
      <MuiMenu
        anchorEl={anchor}
        open={isOpen && Boolean(anchor)}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ sx: overlayStyle, onMouseLeave: hoverTrigger ? () => setOpen(false) : undefined }}
      >
        <Box component="div" sx={{ p: 0 }} onClick={() => clickTrigger && setOpen(false)}>
          {overlayNode}
        </Box>
      </MuiMenu>
    </>
  );
};

export default Dropdown;
